import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { GitBranch, Settings } from 'lucide-react';

interface BranchOption {
  id: string;
  name: string;
  condition: {
    field: string;
    operator: string;
    value: string;
  };
  targetNodeId?: string;
}

interface BranchNodeData {
  title?: string;
  color?: string;
  options?: BranchOption[];
  keywords?: string[];
  defaultPath?: string;
}

export const BranchNode = memo(({ data, isConnectable }: NodeProps<BranchNodeData>) => {
  return (
    <div className="px-4 py-2 shadow-md rounded-md bg-white border-2 border-amber-200 hover:border-amber-400 transition-colors duration-300">
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        className="w-3 h-3 bg-amber-500"
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <GitBranch className="h-4 w-4 mr-2" style={{ color: data.color || '#f59e0b' }} />
          <div className="text-sm font-medium text-gray-900">
            {data.title || 'Rama'}
          </div>
        </div>
        <Settings className="h-4 w-4 text-gray-400" />
      </div>
      
      {/* Options */}
      <div className="mt-2 space-y-2">
        {data.options?.map((option, index) => (
          <div key={option.id} className="text-sm">
            <div className="flex items-center justify-between text-gray-600">
              <span className="font-medium">{option.name}</span>
              <Handle
                type="source"
                position={Position.Bottom}
                id={option.id}
                style={{ left: `${((index + 1) / (data.options?.length || 1 + 1)) * 100}%` }}
                isConnectable={isConnectable}
                className="w-3 h-3 bg-amber-500"
              />
            </div>
            <div className="text-xs text-gray-500">
              {option.condition.field} {option.condition.operator} {option.condition.value}
            </div>
          </div>
        ))}

        {/* Default path */}
        {data.defaultPath && (
          <div className="pt-2 border-t">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>Por defecto</span>
              <Handle
                type="source"
                position={Position.Bottom}
                id="default"
                style={{ left: '90%' }}
                isConnectable={isConnectable}
                className="w-3 h-3 bg-gray-500"
              />
            </div>
          </div>
        )}

        {/* Show placeholder if no options */}
        {(!data.options || data.options.length === 0) && (
          <div className="text-sm text-gray-400 italic">
            Sin condiciones configuradas
          </div>
        )}
      </div>

      {/* Keywords */}
      {data.keywords && data.keywords.length > 0 && (
        <div className="mt-2 pt-2 border-t">
          <div className="flex flex-wrap gap-1">
            {data.keywords.map((keyword, index) => (
              <span 
                key={index}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800"
              >
                {keyword}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

BranchNode.displayName = 'BranchNode';